import {all, call, fork, put, select, takeLatest} from 'redux-saga/effects'
import types from '../actions/types'
import * as dataActions from '../actions/data'
import {
  loadBillingInvoicesFailure,
  loadBillingInvoicesSuccess,
  loadHomeCompanyLocationsFailure,
  loadHomeCompanyLocationsSuccess,
} from 'actions/data'
import * as snackbarActions from '../actions/snackbar'
import {generalError} from '../actions'
import ContactHelper from '../helpers/contacts'
import {apiFunction, simpleApiFunction} from 'helpers/saga'
import {accountHasFeature} from 'helpers/user'
import {features} from 'helpers/constants'
import {getHomeCompanyId, getUser} from 'selectors/auth'
import {DeliveryReasonType} from 'helpers/deliveries'
import {ProductionReasonType} from 'helpers/production'

export default (api) => {
  function * loadTaxCodes () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.taxCode.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const taxCodes = data.result
          yield put(dataActions.loadTaxCodesSuccess(taxCodes))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadTaxCodesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadTaxCodesFailure(e.message, 0))
    }
  }

  function * loadCurrencies () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.currency.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const currencies = data.result
          yield put(dataActions.loadCurrenciesSuccess(currencies))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadCurrenciesFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadCurrenciesFailure(e.message, 0))
    }
  }

  function * loadCountries () {
    try {
      const response = yield call(api.countries.get)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const countries = data.result
          yield put(dataActions.loadCountriesSuccess(countries))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadCountriesFailure(message, code))
        }
      )
    } catch (e) {
      yield put(generalError())
      console.error(e)
    }
  }

  function * loadCompanyGroups () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.companyGroups.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const companyGroups = ContactHelper.getFixedCompanyGroups()
          companyGroups.push.apply(companyGroups, data.result)
          yield put(dataActions.loadCompanyGroupsSuccess(companyGroups))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadCompanyGroupsFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadCompanyGroupsFailure(e.message, 0))
    }
  }

  function * loadBusinessTypes () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.businessTypes.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const businessTypes = data.result
          yield put(dataActions.loadBusinessTypesSuccess(businessTypes))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadBusinessTypesFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadBusinessTypesFailure(e.message, 0))
    }
  }

  function * loadHomeContacts () {
    try {
      const homeCompanyId = yield select(state => state.auth.user.homeCompanyId)
      const params = {
        companyId: homeCompanyId,
        start: 0,
        dormants: true,
        sortColumn: 'forename',
      }

      const fields = { default: true }
      const response = yield call(api.contact.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const homeContacts = data.result
          yield put(dataActions.loadHomeContactsSuccess(homeContacts))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadHomeContactsFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadHomeContactsFailure(e.message, 0))
    }
  }

  function * loadOrderReasons () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.orderReasons.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const orderReasons = data.result
          yield put(dataActions.loadOrderReasonsSuccess(orderReasons))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadOrderReasonsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadOrderReasonsFailure(e.message, 0))
    }
  }

  function * loadOrderTypes () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.orderTypes.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const orderTypes = data.result
          yield put(dataActions.loadOrderTypesSuccess(orderTypes))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadOrderTypesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadOrderTypesFailure(e.message, 0))
    }
  }

  function * loadActivityTypes () {
    try {
      const params = { limit: 1000 }
      const fields = {default: true}
      const response = yield call(api.activityTypes.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          yield put(dataActions.loadActivityTypesSuccess(data))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadActivityTypesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadActivityTypesFailure(e.message, 0))
    }
  }

  function * loadActivityResourceTypes () {
    try {
      const params = { limit: 1000 }
      const fields = {default: true}
      const response = yield call(api.activityResourceTypes.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          yield put(dataActions.loadActivityResourceTypesSuccess(data))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadActivityResourceTypesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadActivityResourceTypesFailure(e.message, 0))
    }
  }

  function * loadActivityResources () {
    try {
      const params = { limit: 1000 }
      const fields = {default: true}
      const response = yield call(api.activityResources.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          yield put(dataActions.loadActivityResourcesSuccess(data))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadActivityResourcesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadActivityResourcesFailure(e.message, 0))
    }
  }

  function * loadActivityReasons () {
    try {
      const params = {}
      const fields = {default: true}
      const response = yield call(api.activityReasons.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(dataActions.loadActivityReasonsSuccess(data))
          },
          function * onFail (key, message, code) {
            yield put(dataActions.loadActivityReasonsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(dataActions.loadActivityReasonsFailure(e.message, 0))
    }
  }

  function * loadCampaigns () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.campaigns.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const campaigns = data.result
          yield put(dataActions.loadCampaignsSuccess(campaigns))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadCampaignsFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadCampaignsFailure(e.message, 0))
    }
  }

  function * loadCampaignGroups () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.campaignGroups.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const campaignGroups = data.result
          yield put(dataActions.loadCampaignGroupsSuccess(campaignGroups))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadCampaignGroupsFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadCampaignGroupsFailure(e.message, 0))
    }
  }

  function * loadContactLists() {
    try {
      const user = yield select(state => state.auth.user)
      if (accountHasFeature(user, features.CONTACT_LISTS)) {
        const params = {}
        const fields = {
          default: true,
          owner: true,
          creator: true,
        }
        const response = yield call(api.contactList.get, params, fields)
        yield api.handleResponse(
          response,
          function* onOk(key, data) {
            const r = data.result
            yield put(dataActions.loadContactListsSuccess(r))
          },
          function* onFail(key, message, code) {
            yield put(dataActions.loadContactListsFailure(message, code))
          }
        )
      }
    } catch (e) {
      yield put(dataActions.loadContactListsFailure(e.message, 0))
    }
  }

  function * loadContactListCategories() {
    try {
      const user = yield select(state => getUser(state))
      if (accountHasFeature(user, features.CONTACT_LISTS)) {
        const params = {}
        const fields = { default: true }
        const response = yield call(api.contactListCategory.get, params, fields)
        yield api.handleResponse(
          response,
          function * onOk (key, data) {
            const products = data.result
            yield put(dataActions.loadContactListCategoriesSuccess(products))
          },
          function * onFail (key, message, code) {
            yield put(dataActions.loadContactListCategoriesFailure(message, code))
          }
        )
      }
    } catch (e) {
      yield put(dataActions.loadContactListCategoriesFailure(e.message, 0))
    }
  }

  function * loadProducts () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.products.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const products = data.result
          yield put(dataActions.loadProductsSuccess(products))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadProductsFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadProductsFailure(e.message, 0))
    }
  }

  const loadTextTemplates = apiFunction(api, api.textTemplates.get,
    function * (key, data) {
      yield put(dataActions.loadTextTemplatesSuccess(data.result))
    },
    function * (key, message, code) {
      yield put(dataActions.loadTextTemplatesFailure(message, code))
    })

  const createTextTemplate = simpleApiFunction(api, api.textTemplates.add,
    dataActions.createTextTemplateSuccess,
    dataActions.createTextTemplateFailure,
    [
      "name",
      "text",
      "typeId",
    ]
  )

  function * loadNominalCodes () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.nominalCodes.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const nominalCodes = data.result
          yield put(dataActions.loadNominalCodesSuccess(nominalCodes))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadNominalCodesFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadNominalCodesFailure(e.message, 0))
    }
  }

  function * loadSendAddresses () {
    try {
      const params = {}
      const fields = { default: true }
      const response = yield call(api.sendAddresses.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk (key, data) {
          const sendAddresses = data.result[0]
          yield put(dataActions.loadSendAddressesSuccess(sendAddresses))
        },
        function * onFail (key, message, code) {
          yield put(dataActions.loadSendAddressesFailure(message, code))
        }
      )
    } catch (e) {
      yield put(dataActions.loadSendAddressesFailure(e.message, 0))
    }
  }

  function * loadQuotationDataFields () {
    try {
      const params = { type: 'order' }
      const fields = {}
      const response = yield call(api.fields.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk(key, data) {
          const fields = data.result
          yield put(dataActions.loadQuoteDataFieldsSuccess(fields))
        },
        function * onFail(key, message, code) {
          yield put(dataActions.loadQuoteDataFieldsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function * loadQuotationItemDataFields () {
    try {
      const params = { type: 'orderItem' }
      const fields = {}
      const response = yield call(api.fields.get, params, fields)
      yield api.handleResponse(
        response,
        function * onOk(key, data) {
          const fields = data.result
          yield put(dataActions.loadQuoteItemDataFieldsSuccess(fields))
        },
        function * onFail(key, message, code) {
          yield put(dataActions.loadQuoteItemDataFieldsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function * loadPurchasingDataFields() {
    try {
      const params = { type: 'purchaseOrder' }
      const fields = {}
      const response = yield call(api.fields.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk(key, data) {
            const fields = data.result
            yield put(dataActions.loadPurchasingFieldsSuccess(fields))
          },
          function * onFail(key, message, code) {
            yield put(dataActions.loadPurchasingFieldsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function * loadPurchasingItemDataFields() {
    try {
      const params = { type: 'purchaseOrderItem' }
      const fields = {}
      const response = yield call(api.fields.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk(key, data) {
            const fields = data.result
            yield put(dataActions.loadPurchasingItemFieldsSuccess(fields))
          },
          function * onFail(key, message, code) {
            yield put(dataActions.loadPurchasingItemFieldsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function * loadAccountPackages () {
    try {
      const response = yield call(api.accounts.getPackages)
      yield api.handleResponse(
        response,
        function * onOk(key, res) {
          const data = res.result
          yield put(dataActions.loadAccountPackagesSuccess(data))
        },
        function * onFail(key, message, code) {
          yield put(dataActions.loadAccountPackagesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function * loadPackages () {
    try {
      const params = {}
      const fields = {
        default: true,
        licences: true,
      }
      const response = yield call(api.accounts.getFeaturePackages, params, fields)
      yield api.handleResponse(
        response,
        function * onOk(key, res) {
          const data = res.result
          yield put(dataActions.loadPackagesSuccess(data))
        },
        function * onFail(key, message, code) {
          yield put(dataActions.loadPackagesFailure(message, code))
        }
      )
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function * loadPackTypes () {
    try {
      const response = yield call(api.packTypes.get)
      yield api.handleResponse(
          response,
          function * onOk(key, res) {
            const data = res.result
            yield put(dataActions.loadPackTypesSuccess(data))
          },
          function * onFail(key, message, code) {
            yield put(dataActions.loadPackTypesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function * loadProductionReasons() {
    try {

      const params = {
        type: ProductionReasonType.onHold,
      }
      const fields = {
        default: true,
      }

      const response = yield call(api.productionReasons.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(dataActions.loadProductionReasonsSuccess(data))
          },
          function * onFail (key, message, code) {
            yield put(dataActions.loadProductionReasonsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function * loadDeliveryReturnReasons() {
    try {

      const params = {
        type: DeliveryReasonType.RETURN,
      }
      const fields = {
        default: true,
      }

      const response = yield call(api.deliveryReasons.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(dataActions.loadDeliveryReturnReasonsSuccess(data))
          },
          function * onFail (key, message, code) {
            yield put(dataActions.loadDeliveryReturnReasonsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function * loadDeliveryMethods() {
    try {

      const params = {}
      const fields = {
        default: true,
      }

      const response = yield call(api.deliveryMethods.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(dataActions.loadDeliveryMethodsSuccess(data))
          },
          function * onFail (key, message, code) {
            yield put(dataActions.loadDeliveryMethodsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function * loadOpportunityStages() {
    try {

      const params = {}
      const fields = {
        default: true,
      }

      const response = yield call(api.opportunityStage.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(dataActions.loadOpportunityStagesSuccess(data))
          },
          function * onFail (key, message, code) {
            yield put(dataActions.loadOpportunityStagesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function * actionFailure ({ message, error }) {
    try {
      yield put(snackbarActions.snackbarMessage(message || error, 'error'))
    } catch (e) {
      yield put(generalError(e.message))
    }
  }

  function * loadHomeCompanyLocations() {

    try {

      const homeCompanyId = yield select(getHomeCompanyId)

      const params = {
        companyId: homeCompanyId,
        sortColumn: 'name',
        sortDirection: 'asc',
      }

      const fields = {
        default: true,
      }

      const response = yield call(api.location.get, params, fields)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(loadHomeCompanyLocationsSuccess(data.result))
          },
          function * onFail (key, message, code) {
            yield put(loadHomeCompanyLocationsFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadBillingInvoices({startingAfter}) {

    try {

      const params = {
        startingAfter,
      }

      const response = yield call(api.billingDetails.getBillingInvoices, params)
      yield api.handleResponse(
          response,
          function * onOk (key, data) {
            yield put(loadBillingInvoicesSuccess(data.result))
          },
          function * onFail (key, message, code) {
            yield put(loadBillingInvoicesFailure(message, code))
          }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function * myWatcher () {
    yield all([
      takeLatest(types.DATA_LOAD_TAX_CODES_ATTEMPT, loadTaxCodes),
      takeLatest(types.DATA_LOAD_CURRENCIES_ATTEMPT, loadCurrencies),
      takeLatest(types.DATA_LOAD_COUNTRIES_ATTEMPT, loadCountries),
      takeLatest(types.DATA_LOAD_COMPANY_GROUPS_ATTEMPT, loadCompanyGroups),
      takeLatest(types.DATA_LOAD_BUSINESS_TYPES_ATTEMPT, loadBusinessTypes),
      takeLatest(types.DATA_LOAD_HOME_CONTACTS_ATTEMPT, loadHomeContacts),
      takeLatest(types.DATA_LOAD_CAMPAIGNS_ATTEMPT, loadCampaigns),
      takeLatest(types.DATA_LOAD_CAMPAIGN_GROUPS_ATTEMPT, loadCampaignGroups),
      takeLatest(types.DATA_LOAD_PRODUCTS_ATTEMPT, loadProducts),
      takeLatest(types.DATA_LOAD_TEXT_TEMPLATES_ATTEMPT, loadTextTemplates),
      takeLatest(types.DATA_LOAD_ORDER_REASONS_ATTEMPT, loadOrderReasons),
      takeLatest(types.DATA_LOAD_ORDER_TYPES_ATTEMPT, loadOrderTypes),
      takeLatest(types.DATA_LOAD_NOMINAL_CODES_ATTEMPT, loadNominalCodes),
      takeLatest(types.DATA_LOAD_CONTACT_LIST_CATEGORIES_ATTEMPT, loadContactListCategories),
      takeLatest(types.DATA_LOAD_CONTACT_LISTS_ATTEMPT, loadContactLists),
      takeLatest(types.DATA_LOAD_SEND_ADDRESSES_ATTEMPT, loadSendAddresses),
      takeLatest(types.DATA_LOAD_QUOTING_DATA_FIELDS_ATTEMPT, loadQuotationDataFields),
      takeLatest(types.DATA_LOAD_QUOTE_ITEM_DATA_FIELDS_ATTEMPT, loadQuotationItemDataFields),
      takeLatest(types.DATA_LOAD_PURCHASING_DATA_FIELDS_ATTEMPT, loadPurchasingDataFields),
      takeLatest(types.DATA_LOAD_PURCHASING_ITEM_DATA_FIELDS_ATTEMPT, loadPurchasingItemDataFields),
      takeLatest(types.DATA_LOAD_ACTIVITY_TYPES_ATTEMPT, loadActivityTypes),
      takeLatest(types.DATA_LOAD_ACTIVITY_RESOURCES_ATTEMPT, loadActivityResources),
      takeLatest(types.DATA_LOAD_ACTIVITY_RESOURCE_TYPES_ATTEMPT, loadActivityResourceTypes),
      takeLatest(types.DATA_LOAD_ACTIVITY_REASONS_ATTEMPT, loadActivityReasons),
      takeLatest(types.DATA_CREATE_TEXT_TEMPLATE_ATTEMPT, createTextTemplate),

      // Failure notifications
      takeLatest(types.CONTACTS_LOAD_CONTACTS_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_COMPANIES_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_DETAILS_CONTACT_LOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_DETAILS_COMPANY_LOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_ACTIVITIES_LOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_DOCUMENTS_LOAD_PARENT_FOLDER_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_DOCUMENTS_LOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_DOCUMENTS_UPLOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_OPPORTUNITIES_LOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_QUOTES_LOAD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_CONTACT_ADD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_CONTACT_EDIT_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_CONTACT_DELETE_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_CONTACT_LIVE_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_CONTACT_DORMANT_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_COMPANY_ADD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_COMPANY_EDIT_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_COMPANY_DELETE_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_COMPANY_LIVE_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_COMPANY_DORMANT_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_GET_FIELD_OPTIONS_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_EXISTING_COMPANIES_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_CONTACT_LISTS_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_CONTACT_LIST_CATEGORY_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_CONTACT_LIST_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOCATION_ADD_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOCATION_EDIT_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOCATION_DELETE_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_NOTIFICATIONS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_ACTIVITY_TYPES_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_COMPANY_DATA_FIELDS_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_CONTACT_DATA_FIELDS_FAILURE, actionFailure),
      takeLatest(types.CONTACTS_LOAD_INDIVIDUAL_DATA_FIELDS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_ACCOUNT_PACKAGES_ATTEMPT, loadAccountPackages),
      takeLatest(types.DATA_LOAD_PACKAGES_ATTEMPT, loadPackages),
      takeLatest(types.DATA_LOAD_PACK_TYPES_ATTEMPT, loadPackTypes),
      takeLatest(types.DATA_LOAD_TAX_CODES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_CURRENCIES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_COUNTRIES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_COMPANY_GROUPS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_BUSINESS_TYPES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_HOME_CONTACTS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_CAMPAIGNS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_PRODUCTS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_TEXT_TEMPLATES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_ORDER_REASONS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_ORDER_TYPES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_NOMINAL_CODES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_CONTACT_LIST_CATEGORIES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_CONTACT_LISTS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_SEND_ADDRESSES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_QUOTING_DATA_FIELDS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_PACKAGES_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_ACTIVITY_REASONS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_PRODUCTION_REASONS_ATTEMPT, loadProductionReasons),
      takeLatest(types.DATA_LOAD_PRODUCTION_REASONS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_DELIVERY_RETURN_REASONS_ATTEMPT, loadDeliveryReturnReasons),
      takeLatest(types.DATA_LOAD_DELIVERY_RETURN_REASONS_FAILURE, actionFailure),
      takeLatest(types.DATA_LOAD_DELIVERY_METHODS_ATTEMPT, loadDeliveryMethods),
      takeLatest(types.DATA_LOAD_DELIVERY_METHODS_FAILURE, actionFailure),
      takeLatest(types.DATA_OPPORTUNITY_STAGES_LOAD_ATTEMPT, loadOpportunityStages),
      takeLatest(types.DATA_OPPORTUNITY_STAGES_LOAD_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_UNSENT_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_UNCONFIRMED_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_NOT_ACCEPTED_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_EXPIRING_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_EXPIRED_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_IN_PRODUCTION_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_REQUIRED_SOON_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_IN_DELIVERY_QUOTES_FAILURE, actionFailure),
      takeLatest(types.QUOTING_LOAD_QUOTE_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_ADD_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_SAVE_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_CHANGE_ORDER_STATUS_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_ADD_REVISION_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_SEND_EMAIL_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_SEND_APPROVAL_EMAIL_FAILURE, actionFailure),
      takeLatest(types.QUOTING_QUOTE_SEND_ACCEPTANCE_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_ADD_CATEGORY_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_UPDATE_CATEGORY_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_DELETE_CATEGORIES_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_REQUERY_LISTS_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_ADD_LIST_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_UPDATE_LIST_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_DORMANT_CONTACTS_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_LOAD_LIST_FAILURE, actionFailure),
      takeLatest(types.CONTACT_LISTS_LOAD_FILTER_VALUES_FAILURE, actionFailure),
      takeLatest(types.DIARY_EDIT_ACTIVITY_FAILURE, actionFailure),
      takeLatest(types.DIARY_ADD_ACTIVITY_FAILURE, actionFailure),
      takeLatest(types.DIARY_EDIT_ACTIVITY_FAILURE, actionFailure),
      takeLatest(types.DIARY_DELETE_ACTIVITY_FAILURE, actionFailure),
      takeLatest(types.DIARY_MOVE_ACTIVITY_FAILURE, actionFailure),
      takeLatest(types.INVOICING_LOAD_INVOICE_FAILURE, actionFailure),
      takeLatest(types.INVOICING_INVOICE_ADD_FAILURE, actionFailure),
      takeLatest(types.INVOICING_INVOICE_SAVE_FAILURE, actionFailure),
      takeLatest(types.INVOICING_INVOICE_UPDATE_INVOICE_FAILURE, actionFailure),
      takeLatest(types.INVOICING_INVOICE_SEND_EMAIL_FAILURE, actionFailure),
      takeLatest(types.INVOICING_INVOICE_UPDATE_FROM_JOB_FAILURE, actionFailure),
      takeLatest(types.INVOICING_MARK_POSTED_FAILURE, actionFailure),
      takeLatest(types.INVOICING_UNMARK_POSTED_FAILURE, actionFailure),
      takeLatest(types.INVOICING_INVOICE_SEND_EMAIL_FAILURE, actionFailure),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_READ_FAILURE, actionFailure),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_UPLOAD_FAILURE, actionFailure),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_UPDATE_FAILURE, actionFailure),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_VALIDATE_FAILURE, actionFailure),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_PRECOMMIT_FAILURE, actionFailure),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_COMMIT_FAILURE, actionFailure),
      takeLatest(types.DELIVERY_NOTE_DIALOG_INITIALISE_FAILURE, actionFailure),
      takeLatest(types.DELIVERIES_ADD_DELIVERY_NOTE_FAILURE, actionFailure),
      takeLatest(types.DELIVERIES_UPDATE_DELIVERY_NOTE_FAILURE, actionFailure),
      takeLatest(types.PURCHASING_LOAD_PURCHASE_ORDER_FAILURE, actionFailure),
      takeLatest(types.PURCHASING_ADD_PURCHASE_ORDER_FAILURE, actionFailure),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_SAVE_FAILURE, actionFailure),
      takeLatest(types.PURCHASING_PURCHASE_ORDER_CHANGE_STATUS_FAILURE, actionFailure),
      takeLatest(types.EMAILING_CONTACT_LIST_POST_FAILURE, actionFailure),
      takeLatest(types.OPPORTUNITY_ADD_FAILURE, actionFailure),
      takeLatest(types.OPPORTUNITY_UPDATE_FAILURE, actionFailure),
      takeLatest(types.ZOHO_STATUS_FAILURE, actionFailure),
      takeLatest(types.ZOHO_AUTHENTICATE_FAILURE, actionFailure),
      takeLatest(types.ZOHO_ACTIVATE_FAILURE, actionFailure),
      takeLatest(types.ZOHO_DEACTIVATE_FAILURE, actionFailure),
      takeLatest(types.ZOHO_GET_WORKSPACES_FAILURE, actionFailure),
      takeLatest(types.ZOHO_GET_RECORDS_FAILURE, actionFailure),
      takeLatest(types.ZOHO_GET_PLAN_FAILURE, actionFailure),
      takeLatest(types.PIVOT_TABLE_LOAD_FAILURE, actionFailure),
      takeLatest(types.SEARCH_FAILURE, actionFailure),
      takeLatest(types.STATE_SAVE_FAILURE, actionFailure),
      takeLatest(types.GENERAL_ERROR, actionFailure),
      takeLatest(types.DEPARTMENTS_LOAD_FAILURE, actionFailure),
      takeLatest(types.DEPARTMENTS_ADD_FAILURE, actionFailure),
      takeLatest(types.DEPARTMENTS_UPDATE_FAILURE, actionFailure),
      takeLatest(types.DEPARTMENTS_DELETE_FAILURE, actionFailure),
      takeLatest(types.WORK_CENTRES_LOAD_FAILURE, actionFailure),
      takeLatest(types.WORK_CENTRES_ADD_FAILURE, actionFailure),
      takeLatest(types.WORK_CENTRES_UPDATE_FAILURE, actionFailure),
      takeLatest(types.WORK_CENTRES_DELETE_FAILURE, actionFailure),
      takeLatest(types.PRICING_CALCULATORS_LOAD_FAILURE, actionFailure),
      takeLatest(types.PRICING_CALCULATOR_MASTER_UPDATE_FAILURE, actionFailure),
      takeLatest(types.PRICING_CALCULATOR_FORM_FETCH_FAILURE, actionFailure),
      takeLatest(types.DATA_HOME_COMPANY_LOCATIONS_LOAD_ATTEMPT, loadHomeCompanyLocations),
      takeLatest(types.DATA_BILLING_INVOICES_LOAD_ATTEMPT, loadBillingInvoices),
    ])
  }

  function * watcher () {
    yield fork(myWatcher)
  }

  return {
    watcher,
  }
}
